import React from 'react';
import { Controller } from 'react-hook-form';
import { DocumentActionRendererProps } from './types';
import { DocumentButton } from '../form/actions/DocumentButton';
import { DocumentAttestation } from '../form/inputs/DocumentAttestation';
import { DocumentCheckbox } from '../form/inputs/DocumentCheckbox';
import { DocumentTagToggle } from '../form/inputs/DocumentTagToggle';
import { Button } from '../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { ActionNode } from '../types';
import { CheckCircle, XCircle } from 'lucide-react';
import {
    createEvaluateCondition,
    executeUpdateRules,
} from '../utils/formUtils';
import { generateReportField } from '../../../services/reportService';
import {
    GenerateReportFieldActionParams,
    ImportFromSchemaActionParams,
    ImportFromSchemaMapping,
    validateActionFunctionParams,
} from '../config/actionFunctionDefinitions';

interface PostActionDialogState {
    open: boolean;
    config: ActionNode['postAction'];
    isSuccess: boolean;
    errorMessage?: string;
}

interface DocumentActionRendererWithDialogProps
    extends DocumentActionRendererProps {
    postActionDialog: PostActionDialogState;
    setPostActionDialog: React.Dispatch<
        React.SetStateAction<PostActionDialogState>
    >;
    validateForComplete?: () => {
        isValid: boolean;
        missingFields: { id: string; title: string }[];
    };
}

const extractInstructionTokens = (instruction: string): string[] => {
    if (!instruction) return [];

    const tokenSet = new Set<string>();
    const tokenRegex = /{{\s*([A-Za-z0-9_.-]+)\s*}}/g;
    let match = tokenRegex.exec(instruction);

    while (match) {
        const token = match[1];
        if (token) {
            tokenSet.add(token);
        }
        match = tokenRegex.exec(instruction);
    }

    return Array.from(tokenSet);
};

const serializeContextValue = (value: any): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint'
    ) {
        return String(value);
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

export const DocumentActionRenderer: React.FC<
    DocumentActionRendererWithDialogProps
> = ({
    field,
    context,
    onSuccess,
    handleCopyNote,
    handlePreview,
    handleSave,
    handleComplete,
    postActionDialog,
    setPostActionDialog,
    validateForComplete,
}) => {
    const {
        control,
        watch,
        setValue,
        updateData,
        checkDisable,
        contextData,
        isEditMode,
    } = context;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState(false);
    const isReadOnly = checkDisable(field.disableWhen);
    const evaluateCondition = createEvaluateCondition(watch, contextData);
    const isEmptyValue = (value: any): boolean =>
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

    // Helper to get default value, prioritizing context data when editing
    const getDefaultValue = (fallback: any) => {
        if (isEditMode && contextData[field.id] !== undefined) {
            return contextData[field.id];
        }
        if (field.data?.default?.fixed !== undefined) {
            return field.data.default.fixed;
        }
        return fallback;
    };

    const getCurrentValue = (key: string) => {
        const watchedValue = watch(key);
        if (watchedValue !== undefined) {
            return watchedValue;
        }
        return contextData[key];
    };

    const getImportParams = (
        actionField: ActionNode
    ): ImportFromSchemaActionParams | null => {
        const actionKey = actionField.functionKey || actionField.id;
        const issues = validateActionFunctionParams({
            functionKey: actionKey,
            functionParams: actionField.functionParams,
            path: '$.functionParams',
        });
        if (issues.length > 0) {
            return null;
        }

        const params = actionField.functionParams;
        if (!params || typeof params !== 'object' || Array.isArray(params)) {
            return null;
        }

        // Normalize to a mappings list: the legacy single pair becomes one
        // mapping; the multi-field form passes mappings directly.
        const mappings: ImportFromSchemaMapping[] = Array.isArray(
            params.mappings
        )
            ? params.mappings
                  .map((mapping: any) => ({
                      sourceAlias:
                          typeof mapping?.sourceAlias === 'string'
                              ? mapping.sourceAlias.trim()
                              : '',
                      targetField:
                          typeof mapping?.targetField === 'string'
                              ? mapping.targetField.trim()
                              : '',
                  }))
                  .filter(
                      (mapping: ImportFromSchemaMapping) =>
                          mapping.sourceAlias && mapping.targetField
                  )
            : [];

        const sourceAlias =
            typeof params.sourceAlias === 'string'
                ? params.sourceAlias.trim()
                : '';
        const targetField =
            typeof params.targetField === 'string'
                ? params.targetField.trim()
                : '';
        if (sourceAlias && targetField) {
            mappings.unshift({ sourceAlias, targetField });
        }

        if (mappings.length === 0) {
            return null;
        }

        return {
            mappings,
            overwriteMode:
                params.overwriteMode === 'always' ? 'always' : 'if-empty',
            condition:
                typeof params.condition === 'string'
                    ? params.condition
                    : undefined,
        };
    };

    const getImportState = (actionField: ActionNode) => {
        const params = getImportParams(actionField);
        if (!params) {
            return { canImport: false, importable: [] as Array<{
                targetField: string;
                sourceValue: any;
            }> };
        }

        const overwriteMode = params.overwriteMode || 'if-empty';
        const conditionMet =
            !params.condition || evaluateCondition(params.condition);

        // A mapping is importable when its source has a value and its target
        // is empty (or overwrite is 'always'). The button is enabled when any
        // mapping is importable and applies all importable ones.
        const importable = conditionMet
            ? (params.mappings || []).flatMap((mapping) => {
                  const sourceValue = getCurrentValue(mapping.sourceAlias);
                  const targetValue = getCurrentValue(mapping.targetField);
                  const sourceHasValue = !isEmptyValue(sourceValue);
                  const targetIsEmpty = isEmptyValue(targetValue);
                  return sourceHasValue &&
                      (overwriteMode === 'always' || targetIsEmpty)
                      ? [{ targetField: mapping.targetField, sourceValue }]
                      : [];
              })
            : [];

        return { canImport: importable.length > 0, importable };
    };

    const runImportFromSchemaAction = (actionField: ActionNode) => {
        const importState = getImportState(actionField);
        if (!importState.canImport) {
            return;
        }

        for (const { targetField, sourceValue } of importState.importable) {
            updateData(targetField, sourceValue);
            setValue(targetField, sourceValue);
        }
    };

    const getGenerateParams = (
        actionField: ActionNode
    ): GenerateReportFieldActionParams | null => {
        const actionKey = actionField.functionKey || actionField.id;
        const issues = validateActionFunctionParams({
            functionKey: actionKey,
            functionParams: actionField.functionParams,
            path: '$.functionParams',
        });
        if (issues.length > 0) {
            return null;
        }

        const params = actionField.functionParams;
        if (!params || typeof params !== 'object' || Array.isArray(params)) {
            return null;
        }

        const instruction =
            typeof params.instruction === 'string'
                ? params.instruction.trim()
                : '';
        const targetField =
            typeof params.targetField === 'string'
                ? params.targetField.trim()
                : '';

        if (!instruction || !targetField) {
            return null;
        }

        return {
            instruction,
            targetField,
            overwriteMode:
                params.overwriteMode === 'always' ? 'always' : 'if-empty',
            condition:
                typeof params.condition === 'string'
                    ? params.condition
                    : undefined,
        };
    };

    const getGenerateState = (actionField: ActionNode) => {
        const generateParams = getGenerateParams(actionField);
        if (!generateParams) {
            return {
                canGenerate: false,
                targetValue: undefined,
            };
        }

        const hasInstruction =
            typeof generateParams.instruction === 'string' &&
            generateParams.instruction.trim().length > 0;
        const hasTargetField =
            typeof generateParams.targetField === 'string' &&
            generateParams.targetField.trim().length > 0;
        if (!hasInstruction || !hasTargetField) {
            return {
                canGenerate: false,
                targetValue: undefined,
            };
        }

        const overwriteMode = generateParams.overwriteMode || 'if-empty';
        const conditionMet =
            !generateParams.condition ||
            evaluateCondition(generateParams.condition);
        const targetValue = getCurrentValue(generateParams.targetField);
        const targetIsEmpty = isEmptyValue(targetValue);
        const canGenerate =
            conditionMet && (overwriteMode === 'always' || targetIsEmpty);

        return {
            canGenerate,
            targetValue,
        };
    };

    const runGenerateReportFieldAction = async (actionField: ActionNode) => {
        const generateParams = getGenerateParams(actionField);
        if (!generateParams) {
            return;
        }

        const generateState = getGenerateState(actionField);
        if (!generateState.canGenerate) {
            return;
        }

        const instruction = generateParams.instruction;
        const tokens = extractInstructionTokens(instruction);
        const values = tokens.reduce((acc, token) => {
            acc[token] = serializeContextValue(getCurrentValue(token));
            return acc;
        }, {} as Record<string, string>);
        const generated = await generateReportField({
            instruction,
            values,
        });
        const nextValue =
            typeof generated?.value === 'string' ? generated.value : '';

        updateData(generateParams.targetField, nextValue);
        setValue(generateParams.targetField, nextValue);
    };

    const commonProps = {
        disabled: isReadOnly,
    };

    const renderSpecificAction = (actionField: ActionNode = field) => {
        if (actionField.actionType === 'button') {
            // Pass validate function only for completeReport button
            const validate =
                (actionField.functionKey || actionField.id) === 'completeReport'
                    ? validateForComplete
                    : undefined;
            const actionKey = actionField.functionKey || actionField.id;
            const isImportAction = actionKey === 'importFromSchema';
            const isImportDisabled = isImportAction
                ? !getImportState(actionField).canImport
                : false;
            const isGenerateAction = actionKey === 'generateReportField';
            const isGenerateDisabled = isGenerateAction
                ? !getGenerateState(actionField).canGenerate
                : false;

            return (
                <DocumentButton
                    field={actionField}
                    onClick={async () => {
                        setIsLoading(true);
                        try {
                            if (actionKey === 'importFromSchema') {
                                runImportFromSchemaAction(actionField);
                                return;
                            }
                            if (actionKey === 'generateReportField') {
                                await runGenerateReportFieldAction(actionField);
                                return;
                            }

                            const actionHandler =
                                context.functions.actions[actionKey];
                            if (actionHandler) {
                                await actionHandler({
                                    action: actionField,
                                    watch,
                                    setValue,
                                    updateData,
                                    data: contextData,
                                });
                                if (
                                    actionKey === 'saveDraft' &&
                                    actionField.postAction
                                ) {
                                    onSuccess(actionField.postAction);
                                }
                            } else if (actionKey === 'copyNote') {
                                await handleCopyNote();
                            } else if (actionKey === 'preview') {
                                handlePreview();
                            } else if (actionKey === 'saveDraft') {
                                await handleSave();
                                if (actionField.postAction) {
                                    onSuccess(actionField.postAction);
                                }
                            } else if (actionKey === 'completeReport') {
                                await handleComplete(actionField.postAction);
                            } else if (actionField.updates) {
                                executeUpdateRules(actionField.updates, {
                                    watch,
                                    setValue,
                                    updateData,
                                    evaluateCondition,
                                });
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    }}
                    disabled={
                        isReadOnly || isImportDisabled || isGenerateDisabled
                    }
                    loading={isLoading}
                    validate={validate}
                />
            );
        }

        if (actionField.actionType === 'checkbox' && actionField.attestation) {
            return (
                <DocumentAttestation field={actionField} context={context} />
            );
        }

        if (actionField.actionType === 'checkbox') {
            return (
                <Controller
                    name={actionField.id}
                    control={control}
                    defaultValue={getDefaultValue(false)}
                    render={({ field: { onChange, value } }) => (
                        <DocumentCheckbox
                            checked={value}
                            onCheckedChange={(val) => {
                                onChange(val);
                                updateData(actionField.id, val);
                                executeUpdateRules(actionField.updates, {
                                    watch,
                                    setValue,
                                    updateData,
                                    evaluateCondition,
                                    localContext: {
                                        value: val,
                                        previousValue: value,
                                    },
                                });
                            }}
                            label={actionField.title}
                            confirmation={actionField.confirmation}
                            {...commonProps}
                            disabled={isReadOnly}
                        />
                    )}
                />
            );
        }

        if (actionField.actionType === 'toggle-tags') {
            return (
                <Controller
                    name={actionField.id}
                    control={control}
                    defaultValue={getDefaultValue([])}
                    render={({ field: { onChange, value } }) => (
                        <DocumentTagToggle
                            value={value}
                            onChange={(val: string[]) => {
                                onChange(val);
                                updateData(actionField.id, val);

                                if (actionField.updates) {
                                    actionField.updates.forEach(
                                        (update: any) => {
                                            const joinedValue =
                                                val.join('\n\n');
                                            updateData(
                                                update.target,
                                                joinedValue
                                            );
                                            setValue(
                                                update.target,
                                                joinedValue
                                            );
                                        }
                                    );
                                }
                            }}
                            options={actionField.data?.options?.fixed || []}
                            {...commonProps}
                        />
                    )}
                />
            );
        }

        return null;
    };

    const actionElement = renderSpecificAction();

    if (!actionElement) return null;

    // Get the current dialog content based on success/error state
    const currentDialogContent = postActionDialog.config
        ? postActionDialog.isSuccess
            ? postActionDialog.config.success
            : postActionDialog.config.error
        : null;

    return (
        <React.Fragment>
            {actionElement}
            {field.postAction && (
                <Dialog
                    open={
                        postActionDialog.open &&
                        postActionDialog.config === field.postAction
                    }
                    onOpenChange={(open) =>
                        setPostActionDialog((prev) => ({ ...prev, open }))
                    }
                >
                    <DialogContent
                        hideCloseButton={currentDialogContent?.hideCloseButton}
                    >
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {postActionDialog.isSuccess ? (
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-red-500" />
                                )}
                                {currentDialogContent?.title}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <p>{currentDialogContent?.message}</p>
                            {!postActionDialog.isSuccess &&
                                postActionDialog.errorMessage && (
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        Error: {postActionDialog.errorMessage}
                                    </p>
                                )}
                        </div>
                        <div className="flex justify-end gap-3">
                            {currentDialogContent?.actions.map(
                                (action, index) => (
                                    <Button
                                        key={index}
                                        variant={
                                            action.value === 'navigate'
                                                ? 'default'
                                                : action.value === 'retry'
                                                ? 'destructive'
                                                : 'outline'
                                        }
                                        onClick={() => {
                                            if (action.value === 'retry') {
                                                setPostActionDialog({
                                                    open: false,
                                                    config: undefined,
                                                    isSuccess: true,
                                                });
                                                // Trigger retry by calling handleComplete again
                                                handleComplete(
                                                    field.postAction
                                                );
                                            } else {
                                                setPostActionDialog({
                                                    open: false,
                                                    config: undefined,
                                                    isSuccess: true,
                                                });
                                                if (
                                                    action.value ===
                                                        'navigate' &&
                                                    action.url
                                                ) {
                                                    navigate(action.url);
                                                }
                                            }
                                        }}
                                    >
                                        {action.label}
                                    </Button>
                                )
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </React.Fragment>
    );
};
